import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { episodeApi } from '../services/user';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

export default function BatchResourceLoadingPage() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['library', 'common']);
  const [status, setStatus] = useState<{
    status: string;
    progress: number;
    message: string;
    result?: any;
  }>({
    status: 'loading',
    progress: 0,
    message: t('library:batch.preparing')
  });
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (!episodeId) {
      setError(t('library:batch.missingEpisodeId'));
      return;
    }

    // 开始轮询状态
    const pollStatus = async () => {
      try {
        const response = await episodeApi.getBatchGenerationStatus(episodeId);

        // 更新状态
        setStatus({
          status: response.status,
          progress: response.progress || 0,
          message: getStatusMessage(response.status, response.progress),
          result: response.result
        });

        // 检查是否完成
        if (response.status === 'completed' || response.episodeStatus === 'video_generation') {
          // 清除轮询
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // 等待一小段时间让用户看到完成状态，然后跳转
          if (!hasNavigatedRef.current) {
            hasNavigatedRef.current = true;
            setTimeout(() => {
              navigate(`/episode/${episodeId}/editor`, { replace: true });
            }, 1500);
          }
        } else if (response.status === 'failed') {
          // 清除轮询
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setError(t('library:batch.failedMessage'));
        }
      } catch (err: any) {
        console.error('查询状态失败:', err);
        setError(err.message || t('library:batch.queryFailed'));
        // 清除轮询
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    // 立即执行一次
    pollStatus();

    // 设置轮询（每2秒）
    pollingIntervalRef.current = setInterval(pollStatus, 2000);

    // 清理函数
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [episodeId, navigate]);

  const getStatusMessage = (status: string, progress?: number): string => {
    switch (status) {
      case 'not_started':
        return t('library:batch.statusMessages.notStarted');
      case 'waiting':
        return t('library:batch.statusMessages.waiting');
      case 'active':
        const msg = t('library:batch.statusMessages.active');
        return progress ? `${msg} (${progress}%)` : msg;
      case 'completed':
        return t('library:batch.completed');
      case 'failed':
        return t('library:batch.failedMessage');
      default:
        return t('library:batch.statusMessages.active');
    }
  };

  const handleRetry = () => {
    if (episodeId) {
      navigate(`/episode/${episodeId}/script-generation`, { replace: true });
    }
  };

  const handleSkip = () => {
    if (episodeId) {
      navigate(`/episode/${episodeId}/editor`, { replace: true });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-bg-surface rounded-2xl border border-border-subtle p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mb-4">
              <XCircleIcon className="w-10 h-10 text-error" />
            </div>
            <h2 className="text-xl font-bold text-gray-50 mb-2">{t('library:batch.failed')}</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleRetry}
                className="flex-1 px-4 py-2 bg-bg-elevated hover:bg-border-default text-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                {t('library:batch.backToScript')}
              </button>
              <button
                onClick={handleSkip}
                className="flex-1 px-4 py-2 bg-info hover:bg-info/80 text-white rounded-lg transition-colors cursor-pointer"
              >
                {t('library:batch.continueEdit')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-page flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-bg-surface rounded-2xl border border-border-subtle p-8">
        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-info/20 rounded-full flex items-center justify-center mb-6 relative">
            {status.status === 'completed' ? (
              <CheckCircleIcon className="w-12 h-12 text-success" />
            ) : (
              <>
                <ArrowPathIcon className="w-12 h-12 text-info animate-spin" />
                <div className="absolute inset-0 rounded-full border-2 border-info/20 animate-ping" />
              </>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-50 mb-2">
            {status.status === 'completed' ? t('library:batch.titleCompleted') : t('library:batch.title')}
          </h2>

          {/* Description */}
          <p className="text-gray-400 mb-8">
            {status.status === 'completed'
              ? t('library:batch.subtitleCompleted')
              : t('library:batch.subtitle')}
          </p>

          {/* Progress Bar */}
          <div className="w-full mb-6">
            <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
              <span>{status.message}</span>
              <span className="font-mono">{Math.round(status.progress)}%</span>
            </div>
            <div className="w-full bg-bg-elevated rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-info to-info/80 transition-all duration-500 ease-out rounded-full relative overflow-hidden"
                style={{ width: `${status.progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              </div>
            </div>
          </div>

          {/* Friendly Tips */}
          <div className="mt-6 p-4 bg-info/10 border border-info/30 rounded-lg">
            <p className="text-sm text-info">
              {t('library:batch.tip')}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
